import React from 'react'
import { useParams } from 'react-router-dom'
import { useCart } from '../state/CartContext'
import { getProduct, type ApiProduct } from '../api/caApi'
import { useI18n } from '../state/I18nContext'
import BadgePills from '../components/BadgePills'
import AddToCartButton from '../components/AddToCartButton'

const pickPrimaryImage = (p: ApiProduct): string => {
  const first = p.photos.slice().sort((a, b) => a.sort - b.sort)[0]
  return first?.url || '/apps/ca/img/decor/product_bread_03.jpg'
}

const Product: React.FC = () => {
  const { id } = useParams()
  const { add } = useCart()
  const { t, locale } = useI18n()

  const [product, setProduct] = React.useState<ApiProduct | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      const slug = id || ''
      if (!slug) {
        setError(t.product.missingId)
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError(null)
        const p = await getProduct(slug, { lang: locale })
        if (!alive) return
        setProduct(p)
      } catch (e: any) {
        if (!alive) return
        const code = String(e?.code || 'not_found') as keyof typeof t.apiErrors
        const mapped = (t.apiErrors as any)[code] as string | undefined
        setError(mapped || t.product.notFound)
        setProduct(null)
      } finally {
        if (!alive) return
        setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [id, locale, t])

  if (loading) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-2xl font-bold">{t.product.loading}</h1>
      </section>
    )
  }

  if (error || !product) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-2xl font-bold">{t.product.notFound}</h1>
        <p className="mt-2 text-neutral-700">{error || t.product.tryCatalog}</p>
      </section>
    )
  }

  return (
    <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-8">
      <div className="rounded-xl overflow-hidden shadow border border-neutral-200">
        <img src={pickPrimaryImage(product)} alt={product.name} className="w-full h-full object-cover" />
      </div>
      <div>
        <h1 className="text-3xl font-bold">{product.name}</h1>
        <BadgePills badges={product.badges} className="mt-3" />
        {product.summary ? (
          <p className="mt-3 text-neutral-700">{product.summary}</p>
        ) : null}
        <p className="mt-6 text-2xl font-extrabold text-amber-700">€ {product.price.toFixed(2)}</p>
        <div className="mt-6 flex gap-3">
          <AddToCartButton
            className="rounded-lg bg-amber-600 text-white px-5 py-3 font-semibold hover:bg-amber-700"
            onAdd={() => add({ id: product.slug, name: product.name, price: product.price, image: pickPrimaryImage(product) })}
          >
            {t.product.addToCart}
          </AddToCartButton>
        </div>
        {product.description ? (
          <div
            className="mt-10 text-sm text-neutral-700 prose prose-neutral max-w-none"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
        ) : null}

        <div className="mt-4">
          <small className="text-xs text-gray-500">{t.product.packagingNote}</small>
        </div>
      </div>
    </section>
  )
}

export default Product
